/**
 * usePerformanceData Hook
 * 
 * React Query hook for fetching performance data.
 * Migrated from seo-engine-webtools-customer-module
 */

import { useQuery } from '@tanstack/react-query';
import { getPerformanceData, getDailyAggregates, GetPerformanceParams, DailyAggregatesResponse } from '@/services/customer/performanceApi';
import { PerformanceDataResponse } from '@/types/customer';
import { useSiteStore } from '@/store/site.store';

export interface UsePerformanceDataOptions extends GetPerformanceParams {
  siteId?: number;
  enabled?: boolean;
  page?: number;
  size?: number;
}

export function usePerformanceData(options: UsePerformanceDataOptions) {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = options.siteId ?? selectedSite?.id ?? 1;

  return useQuery<PerformanceDataResponse, Error>({
    queryKey: ['customer-performance', siteId, options.startDate, options.endDate, options.engineTypes, options.dimensions, options.page, options.size],
    queryFn: () => getPerformanceData(siteId, {
      startDate: options.startDate,
      endDate: options.endDate,
      engineTypes: options.engineTypes,
      dimensions: options.dimensions,
      limit: options.limit,
      page: options.page,
      size: options.size,
    }),
    enabled: options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export interface UseDailyAggregatesOptions {
  siteId?: number;
  startDate: string;
  endDate: string;
  device?: string;
  country?: string;
  enabled?: boolean;
}

export function useDailyAggregates(options: UseDailyAggregatesOptions) {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = options.siteId ?? selectedSite?.id ?? 1;

  return useQuery<DailyAggregatesResponse, Error>({
    queryKey: ['daily-aggregates', siteId, options.startDate, options.endDate, options.device, options.country],
    queryFn: () => getDailyAggregates(siteId, options.startDate, options.endDate, options.device, options.country),
    enabled: options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export default usePerformanceData;
